import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShoppingBag, Check, Truck, Gift, Sparkles, ArrowRight, Instagram } from "lucide-react";
import { formatINR } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { useCart } from "@/context/CartContext";
import { useInstagram } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductCard } from "@/components/wp/ProductCard";
import { ProductGallery } from "@/components/wp/ProductGallery";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useGet(`/products/${id}`, [id]);
  const { addItem, addRecentlyViewed, recentlyViewed } = useCart();

  const product = data?.product;
  const suggested = data?.suggested || [];
  const ig = useInstagram();

  useEffect(() => { if (product) addRecentlyViewed({ ...product, type: "product" }); }, [product, addRecentlyViewed]);

  const onAdd = () => {
    addItem({ id: product.id, name: product.name, price: product.price, image: product.image, type: "product" });
    toast.success(`${product.name} added 💝`);
  };

  if (loading) return (
    <div className="wp-container grid gap-8 py-10 lg:grid-cols-2">
      <Skeleton className="aspect-square w-full rounded-wp-xl" />
      <div className="space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-24 w-full" /></div>
    </div>
  );
  if (!product) return <div className="wp-container py-20 text-center">Not found. <Link to="/shop" className="text-wp-rose-strong underline">Back to shop</Link></div>;

  const recent = (recentlyViewed || []).filter((r) => r.id !== product.id).slice(0, 4);

  return (
    <div className="wp-container py-8 sm:py-10">
      <nav className="mb-5 text-xs text-wp-ink-muted"><Link to="/" className="hover:text-wp-rose-strong">Home</Link> / <Link to="/shop" className="hover:text-wp-rose-strong">Shop</Link> / <span className="text-wp-ink">{product.name}</span></nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <ProductGallery images={[product.image, ...(product.images || [])]} alt={product.name} testid="product-gallery" />
        <div>
          <Badge className="mb-2 bg-wp-blush-100 text-wp-rose-strong border-0">{product.category}</Badge>
          <h1 className="font-display text-3xl sm:text-4xl">{product.name}</h1>
          <span className="price-num mt-3 block font-display text-3xl text-wp-rose-strong">{formatINR(product.price)}</span>
          <p className="mt-4 text-sm leading-relaxed text-wp-ink-muted">{product.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(product.colors || []).map((c) => <span key={c} className="wp-chip capitalize">{c}</span>)}
          </div>
          <div className="mt-6 rounded-wp-lg border border-border bg-card p-4 text-sm shadow-wp-soft">
            <p className="flex items-center gap-2"><Gift className="h-4 w-4 text-wp-rose-strong" /> Add this to any parcel — we’ll pack it gift-ready.</p>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button onClick={onAdd} data-testid="product-add-to-cart-button" className="flex-1 rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-105">
              <ShoppingBag className="mr-2 h-5 w-5" /> Add to Cart · {formatINR(product.price)}
            </Button>
            <Button onClick={() => navigate("/build")} variant="outline" className="rounded-xl py-6" data-testid="product-build-parcel-button">
              <Sparkles className="mr-2 h-4 w-4 text-wp-ocean" /> Build a parcel
            </Button>
          </div>
          <div className="mt-5 flex flex-wrap gap-4 text-xs text-wp-ink-muted">
            <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-wp-rose-strong" /> Ships in 24–48h</span>
            <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-wp-success" /> Hand-packed with love</span>
          </div>
        </div>
      </div>

      {ig.enabled && (
        <a href={ig.url} target="_blank" rel="noreferrer" data-testid="product-instagram-cta"
          className="mt-10 flex items-center gap-4 rounded-wp-lg border border-border bg-wp-blush-50 p-5 shadow-wp-soft transition-transform hover:-translate-y-0.5">
          <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-white text-wp-rose-strong shadow"><Instagram className="h-6 w-6" /></span>
          <div className="flex-1">
            <p className="text-sm font-semibold">See real customer photos & behind the scenes</p>
            <p className="text-xs text-wp-ink-muted">Follow us on Instagram {ig.handle}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-wp-rose-strong" />
        </a>
      )}

      {suggested.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl">Complete the look</h2>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {suggested.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {recent.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-xl">Recently viewed</h2>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {recent.map((r) => (
              <Link key={r.id} to={`/${r.type === "parcel" ? "parcel" : "product"}/${r.type === "parcel" ? r.slug : r.id}`}
                className="w-32 flex-shrink-0 overflow-hidden rounded-wp-md border border-border bg-card shadow-wp-soft">
                <img src={r.image} alt={r.name} className="aspect-square w-full object-cover" />
                <div className="p-2"><p className="line-clamp-1 text-xs font-medium">{r.name}</p><p className="price-num text-xs text-wp-rose-strong">{formatINR(r.price)}</p></div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
